import React from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { ScrollPanel } from 'primereact/scrollpanel';
import { RadioButton } from 'primereact/radiobutton';
import Draggable from 'react-draggable';

interface Step3PositionsProps {
    documentRecipients: any[];
    signaturePositions: any[];
    selectedSigner: any;
    setSelectedSigner: (signer: any) => void;
    isViewMode?: boolean;
    canvasLoading: boolean;
    pageCanvases: any[];
    canvasRefs: React.MutableRefObject<any[]>;
    draggableRefs: React.MutableRefObject<Record<string, React.RefObject<any>>>;
    handleDrop: (event: React.DragEvent, pageIndex: number) => void;
    handleDragStop: (event: any, data: any, locationSeqId: string) => void;
    selectedSignatureType: string;
    setSelectedSignatureType: (type: string) => void;
    removeSignaturePosition: (id: string) => void;
    setSignaturePositions: (pos: any[]) => void;
    normalizeLocationSeq: (pos: any[]) => any[];
    isTemplateMode?: boolean;
    mainFiles: any[];
    activeMainFileIndex: number;
    onSetActiveFile: (index: number) => void;
}

export const Step3Positions: React.FC<Step3PositionsProps> = ({
    documentRecipients,
    signaturePositions,
    selectedSigner,
    setSelectedSigner,
    isViewMode = false,
    canvasLoading,
    pageCanvases,
    canvasRefs,
    draggableRefs,
    handleDrop,
    handleDragStop,
    selectedSignatureType,
    setSelectedSignatureType,
    removeSignaturePosition,
    setSignaturePositions,
    normalizeLocationSeq,
    isTemplateMode = false,
    mainFiles = [],
    activeMainFileIndex = 0,
    onSetActiveFile
}) => {
    const activeFile = mainFiles[activeMainFileIndex];
    return (
        <div className="flex flex-column lg:flex-row h-full gap-0 overflow-hidden" style={{ minHeight: '600px', background: 'var(--surface-ground)' }}>
            {/* Left Panel: Signer Selection and Position List */}
            <div className="col-12 lg:col-3 flex flex-column gap-3 p-3 overflow-y-auto border-right-1 surface-border shadow-2" style={{ maxHeight: 'calc(100vh - 120px)', background: 'var(--surface-section)' }}>
                <div className="flex flex-column gap-1 mb-3">
                    <label className="text-color text-xs font-bold opacity-60 uppercase mb-2">1. Chọn người ký</label>
                    <Dropdown
                        className="w-full"
                        style={{ background: 'var(--surface-section)', color: 'var(--text-color)' }}
                        optionLabel="label"
                        options={documentRecipients.map(r => ({
                            value: r._internalId,
                            _internalId: r._internalId,
                            partyId: r.party?.partyId || r.partyId,
                            roleTypeId: r.roleTypeId,
                            label: r.party?.label || r.partyName || 'Chưa chọn đối tượng'
                        }))}
                        value={selectedSigner?._internalId || null}
                        onChange={(e) => {
                            const opt = documentRecipients.map(r => ({ 
                                value: r._internalId,
                                _internalId: r._internalId,
                                partyId: r.party?.partyId || r.partyId, 
                                roleTypeId: r.roleTypeId, 
                                label: r.party?.label || r.partyName 
                            })).find(o => o.value === e.value);
                            setSelectedSigner(opt as any);
                        }}
                        placeholder="Chọn người ký"
                        disabled={isViewMode}
                    />
                </div>
                
                {!isViewMode && !isTemplateMode && selectedSigner && (
                    <div className="mb-4 surface-card p-3 border-round-lg border-1 surface-border">
                        <label className="text-color text-xs font-bold opacity-60 uppercase block mb-3">2. Công cụ thiết lập</label>
                        <Button
                            icon="pi pi-plus"
                            label="Thêm vị trí ký"
                            severity="danger"
                            className="w-full shadow-4 p-button-lg font-bold"
                            style={{ background: '#ef4444', border: 'none' }}
                            draggable
                            onDragStart={(event) => {
                                event.dataTransfer.setData('text/plain', 'electronic-signature');
                            }}
                        />
                        <div className="flex mt-3 align-items-center gap-2 text-color-secondary opacity-60">
                            <i className="pi pi-info-circle text-xs"></i>
                            <small className="italic text-xs">Kéo thả nút trên vào vùng cần ký</small>
                        </div>

                        <Divider className="surface-border my-3" />
                        
                        <div className="flex flex-column gap-2">
                            <label className="text-color-secondary opacity-60" style={{ fontSize: '11px' }}>Loại chữ ký sẽ tạo</label>
                            <div className="flex flex-wrap gap-2">
                                <div 
                                    className={`flex-1 min-w-min cursor-pointer border-round-md p-2 flex flex-column align-items-center gap-1 border-1 transition-all transition-duration-200 ${selectedSignatureType === 'AgtDigitalVerified' ? 'bg-primary-reverse shadow-2 border-primary' : 'bg-surface-100 border-surface-200 opacity-60'}`}
                                    onClick={() => setSelectedSignatureType('AgtDigitalVerified')}
                                >
                                    <i className={`pi pi-verified ${selectedSignatureType === 'AgtDigitalVerified' ? 'text-primary' : 'text-color-secondary'}`} style={{ fontSize: '18px' }}></i>
                                    <span className={`font-bold ${selectedSignatureType === 'AgtDigitalVerified' ? 'text-primary' : 'text-color-secondary'}`} style={{ fontSize: '10px' }}>KÝ SỐ</span>
                                </div>
                                <div 
                                    className={`flex-1 min-w-min cursor-pointer border-round-md p-2 flex flex-column align-items-center gap-1 border-1 transition-all transition-duration-200 ${(selectedSignatureType === 'AgtImageVerified' || selectedSignatureType === 'AgtHandwritingVerified') ? 'bg-primary-reverse shadow-2 border-primary' : 'bg-surface-100 border-surface-200 opacity-60'}`}
                                    onClick={() => setSelectedSignatureType('AgtImageVerified')}
                                >
                                    <i className={`pi pi-image ${(selectedSignatureType === 'AgtImageVerified' || selectedSignatureType === 'AgtHandwritingVerified') ? 'text-primary' : 'text-color-secondary'}`} style={{ fontSize: '18px' }}></i>
                                    <span className={`font-bold ${(selectedSignatureType === 'AgtImageVerified' || selectedSignatureType === 'AgtHandwritingVerified') ? 'text-primary' : 'text-color-secondary'}`} style={{ fontSize: '10px' }}>KÝ ẢNH</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <Divider className="surface-border my-2" />

                <div className="flex-1 flex flex-column gap-3">
                    <span className="text-color text-xs font-bold opacity-60 uppercase mb-2">
                        Vị trí ký hiện có ({signaturePositions.filter(p => 
                            (p.agreementContentId && activeFile?.agreementContentId ? p.agreementContentId === activeFile.agreementContentId : (!p.fileId || p.fileId === activeFile?.fileId))
                        ).length})
                    </span>
                    <div className="flex flex-column gap-3">
                        {signaturePositions
                            .filter(p => (p.agreementContentId && activeFile?.agreementContentId ? p.agreementContentId === activeFile.agreementContentId : (!p.fileId || p.fileId === activeFile?.fileId)))
                            .map((position, index) => (
                            <div key={index} className="flex flex-column gap-2 mb-3" style={isViewMode ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
                                <div className="flex justify-content-between align-items-center mb-1">
                                    <b className="text-color" style={{ fontSize: '13px' }}>Vị trí {index + 1}: {(() => {
                                        const recipient = documentRecipients.find(r => r._internalId === position.internalRecipientId);
                                        const rawLabel = recipient?.party?.label || recipient?.partyName || position.partyName || position.label || 'Chưa chọn đối tượng';
                                        const match = String(rawLabel).match(/\]\s*-\s*(.+)$/);
                                        return match ? match[1] : rawLabel;
                                    })()}</b>
                                    {!isViewMode && !isTemplateMode && (
                                        <Button icon="pi pi-times" text outlined severity="danger" size="small" className="h-1.5rem w-1.5rem p-0" onClick={() => removeSignaturePosition(position.locationSeqId)} />
                                    )}
                                </div>
                                    <div className="flex flex-column col-12 p-0 mt-2">
                                        <label className="text-color-secondary opacity-60 mb-1" style={{ fontSize: '11px' }}>Loại chữ ký</label>
                                        <Dropdown 
                                            value={position.signatureTypeEnumId === 'AgtHandwritingVerified' ? 'AgtImageVerified' : (position.signatureTypeEnumId || 'AgtDigitalVerified')} 
                                            options={[
                                                { label: 'Ký số ', value: 'AgtDigitalVerified', icon: 'pi pi-verified' },
                                                { label: 'Ký ảnh', value: 'AgtImageVerified', icon: 'pi pi-image' }
                                            ]}
                                            onChange={(e) => {
                                                const newPos = [...signaturePositions];
                                                newPos[index].signatureTypeEnumId = e.value;
                                                
                                                // Tự động điều chỉnh kích thước nếu cần
                                                if (e.value === 'AgtImageVerified') {
                                                    newPos[index].widthPx = 200;
                                                    newPos[index].heightPx = 100;
                                                } else {
                                                    newPos[index].widthPx = 200;
                                                    newPos[index].heightPx = 65;
                                                }
                                                
                                                setSignaturePositions(normalizeLocationSeq(newPos));
                                            }}
                                            className="p-inputtext-sm w-full"
                                            style={{ background: 'var(--surface-ground)', fontSize: '12px' }}
                                            itemTemplate={(option) => (
                                                <div className="flex align-items-center gap-2">
                                                    <i className={option.icon} style={{ fontSize: '12px' }}></i>
                                                    <span style={{ fontSize: '12px' }}>{option.label}</span>
                                                </div>
                                            )}
                                        />
                                    </div>
                                    <div className="flex gap-2 mt-2">
                                        <div className="flex flex-column col-4 p-0">
                                            <label className="text-color-secondary opacity-60 mb-1" style={{ fontSize: '11px' }}>Rộng</label>
                                            <InputText type="number" value={position.signatureTypeEnumId === 'AgtDigitalVerified' ? (position.widthPx || 200) : 200} size="small" className="p-inputtext-sm w-full" style={{ background: 'var(--surface-ground)', fontSize: '11px' }} disabled={isViewMode || position.signatureTypeEnumId !== 'AgtDigitalVerified'} 
                                                onChange={(e) => {
                                                    const newPos = [...signaturePositions];
                                                    const val = parseInt(e.target.value) || 0;
                                                    const canvas = pageCanvases[position.pageIndex];
                                                    const maxW = canvas ? canvas.width - (position.x || 0) : 1000;
                                                    newPos[index].widthPx = Math.min(Math.max(val, 0), maxW);
                                                    setSignaturePositions(normalizeLocationSeq(newPos));
                                                }}
                                            />
                                        </div>
                                        <div className="flex flex-column col-4 p-0">
                                            <label className="text-color-secondary opacity-60 mb-1" style={{ fontSize: '11px' }}>Cao</label>
                                            <InputText type="number" value={position.signatureTypeEnumId === 'AgtDigitalVerified' ? (position.heightPx || 65) : 100} size="small" className="p-inputtext-sm w-full" style={{ background: 'var(--surface-ground)', fontSize: '11px' }} disabled={isViewMode || position.signatureTypeEnumId !== 'AgtDigitalVerified'}
                                                onChange={(e) => {
                                                    const newPos = [...signaturePositions];
                                                    const val = parseInt(e.target.value) || 0;
                                                    const canvas = pageCanvases[position.pageIndex];
                                                    const maxH = canvas ? canvas.height - (position.y || 0) : 1000;
                                                    newPos[index].heightPx = Math.min(Math.max(val, 0), maxH);
                                                    setSignaturePositions(normalizeLocationSeq(newPos));
                                                }}
                                            />
                                        </div>
                                        <div className="flex flex-column col-4 p-0">
                                            <label className="text-color-secondary opacity-60 mb-1" style={{ fontSize: '11px' }}>Chữ</label>
                                            <InputText type="number" value={position.fontSize || 12} size="small" className="p-inputtext-sm w-full" style={{ background: 'var(--surface-ground)', fontSize: '11px' }} disabled={isViewMode}
                                                onChange={(e) => {
                                                    const newPos = [...signaturePositions];
                                                    const val = parseInt(e.target.value) || 0;
                                                    newPos[index].fontSize = Math.max(val, 1);
                                                    setSignaturePositions(normalizeLocationSeq(newPos));
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                    {signaturePositions.length === 0 && (
                        <div className="p-5 text-center text-color-secondary opacity-20 border-1 border-dashed surface-border border-round-lg flex flex-column align-items-center gap-2">
                            <i className="pi pi-map-marker text-2xl"></i>
                            <span className="text-xs">Chưa có vị trí ký nào được xác định</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Middle Panel: PDF Preview */}
            <div className="flex-1 flex flex-column relative overflow-hidden bg-surface-200">
                {canvasLoading && (
                    <div className="flex-1 flex flex-column align-items-center justify-content-center bg-white-alpha-40" style={{ zIndex: 10 }}>
                        <i className="pi pi-spin pi-spinner text-4xl text-blue-500"></i>
                        <p className="mt-2 font-medium text-700">Đang xử lý PDF...</p>
                    </div>
                )}

                {!canvasLoading && pageCanvases.length > 0 && (
                    <ScrollPanel className="flex-1 w-full custom-scroll" style={{ height: '0' }}>
                        <div className="py-5 px-3 flex flex-column align-items-center min-h-full" style={{ background: 'var(--surface-200)' }}>
                            {pageCanvases.map((canvas, index) => (
                                <React.Fragment key={index}>
                                    <div
                                        data-page-index={index}
                                        className="shadow-8 bg-white mb-2 relative"
                                        onDrop={(event) => handleDrop(event, index)}
                                        onDragOver={(event) => event.preventDefault()}
                                        style={{ width: 'fit-content' }}
                                    >
                                        <canvas ref={ref => {
                                            if (ref && canvas) {
                                                const context = ref.getContext('2d');
                                                if (context) {
                                                    ref.width = canvas.width;
                                                    ref.height = canvas.height;
                                                    context.drawImage(canvas, 0, 0);
                                                    canvasRefs.current[index] = ref;
                                                }
                                            }
                                        }} className="block" style={{ maxWidth: '100%', height: 'auto' }}></canvas>

                                        {signaturePositions
                                            .filter(element => Number(element.pageIndex) === index && (element.agreementContentId && activeFile?.agreementContentId ? element.agreementContentId === activeFile.agreementContentId : (!element.fileId || element.fileId === activeFile?.fileId)))
                                            .map((element) => {
                                                const nodeRef = draggableRefs.current[element.locationSeqId] || (draggableRefs.current[element.locationSeqId] = React.createRef());
                                                return (
                                                    <Draggable
                                                        nodeRef={nodeRef}
                                                        key={element.locationSeqId}
                                                        defaultPosition={{ x: element.x || 0, y: element.y || 0 }}
                                                        bounds="parent"
                                                        disabled={isViewMode}
                                                        onStop={(event, data) => handleDragStop(event, data, element.locationSeqId)}
                                                    >
                                                        <div
                                                            ref={nodeRef}
                                                            style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                background: 'rgb(255,255,255)',
                                                                padding: '5px',
                                                                borderRadius: '5px',
                                                                cursor: isViewMode ? 'default' : 'move',
                                                                borderColor: 'red',
                                                                borderWidth: '2px',
                                                                borderStyle: 'dashed',
                                                                height: element.signatureTypeEnumId === 'AgtDigitalVerified' ? (element.heightPx || 65) : 100,
                                                                width: element.signatureTypeEnumId === 'AgtDigitalVerified' ? (element.widthPx || 200) : 200,
                                                                minWidth: element.signatureTypeEnumId === 'AgtDigitalVerified' ? (element.widthPx || 200) : 200,
                                                                maxWidth: element.signatureTypeEnumId === 'AgtDigitalVerified' ? (element.widthPx || 200) : 200,
                                                                minHeight: element.signatureTypeEnumId === 'AgtDigitalVerified' ? (element.heightPx || 65) : 100,
                                                                maxHeight: element.signatureTypeEnumId === 'AgtDigitalVerified' ? (element.heightPx || 65) : 100,
                                                                overflow: 'hidden',
                                                                zIndex: 100
                                                            }}
                                                            className={'flex shadow-2'}
                                                        >
                                                            <div className={'col-11 h-full w-full flex flex-column relative'} style={{ fontSize: element.fontSize, padding: 0, color: '#000', lineHeight: 1.2 }}>
                                                                <div className="absolute top-0 right-0 p-1 opacity-20">
                                                                    <i className={`pi ${(element.signatureTypeEnumId === 'AgtHandwritingVerified' || element.signatureTypeEnumId === 'AgtImageVerified') ? 'pi-image' : 'pi-verified'}`} style={{ fontSize: '1.2rem' }}></i>
                                                                </div>
                                                                <div className="flex-1 flex flex-column justify-content-center align-items-center text-center px-1">
                                                                    {(() => {
                                                                        const recipient = documentRecipients.find(r => r._internalId === element.internalRecipientId);
                                                                        const rawLabel = recipient?.party?.label || recipient?.partyName || element.partyName || element.label || 'Chưa chọn đối tượng';
                                                                        const match = String(rawLabel).match(/\]\s*-\s*(.+)$/);
                                                                        const label = match ? match[1] : rawLabel;
                                                                        
                                                                        if (element.signatureTypeEnumId === 'AgtHandwritingVerified' || element.signatureTypeEnumId === 'AgtImageVerified') {
                                                                            return (
                                                                                <span 
                                                                                    className="font-bold border-bottom-1 surface-border pb-1 mb-1 block w-full opacity-80" 
                                                                                    style={{ 
                                                                                        fontSize: '0.9em',
                                                                                        whiteSpace: 'nowrap',
                                                                                        overflow: 'hidden',
                                                                                        textOverflow: 'ellipsis'
                                                                                    }}
                                                                                    title={label}
                                                                                >
                                                                                    {label}
                                                                                </span>
                                                                            );
                                                                        }
                                                                        
                                                                        return (
                                                                            <div className="text-left w-full h-full">
                                                                                <span className="font-bold">[KÝ SỐ]</span><br />
                                                                                - Ký bởi: {label}<br />
                                                                                - Ngày ký: dd/MM/yyyy
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Draggable>
                                                );
                                            })}
                                    </div>
                                    {/* Page Number Indicator between pages */}
                                    <div className="text-600 font-bold text-lg my-4 bg-white border-circle w-3rem h-3rem flex align-items-center justify-content-center shadow-4 border-1 border-300">
                                        {index + 1}
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    </ScrollPanel>
                )}

                {!canvasLoading && pageCanvases.length === 0 && (
                    <div className="flex-1 flex flex-column align-items-center justify-content-center text-500 bg-gray-50">
                        <i className="pi pi-file-pdf text-6xl mb-3 opacity-20"></i>
                        <span className="font-medium text-lg">Vui lòng quay lại bước 1 để tải tệp tài liệu chính</span>
                    </div>
                )}
            </div>

            {/* Right Panel: Files Accordion & Thumbnails */}
            <div className="hidden lg:flex col-12 lg:col-3 flex-column gap-0 overflow-hidden border-left-1 surface-border shadow-2" style={{ maxHeight: 'calc(100vh - 120px)', background: 'var(--surface-section)' }}>
                {/* Section Header */}
                <div className="bg-primary p-3 text-center text-white font-bold text-lg shadow-2" style={{ backgroundColor: '#3B82F6' }}>
                    Tệp ký
                </div>

                {/* Download All Banner */}
                {/* Vertical File List (Accordion) */}
                <div className="flex-1 overflow-y-auto p-2 flex flex-column gap-1 bg-surface-ground">
                    {mainFiles.map((file, idx) => {
                        const isActive = activeMainFileIndex === idx;
                        return (
                            <div key={idx} className="flex flex-column surface-card border-bottom-1 surface-border overflow-hidden shadow-1">
                                {/* File Header */}
                                <div 
                                    onClick={() => onSetActiveFile(idx)}
                                    className={`flex align-items-center justify-content-between p-3 cursor-pointer transition-all hover:bg-black-alpha-5 ${isActive ? 'bg-primary-50' : ''}`}
                                >
                                    <div className="flex align-items-start gap-2 flex-1 overflow-hidden">
                                        <i className={`pi pi-file-pdf mt-1 ${isActive ? 'text-primary' : 'text-color-secondary'}`} style={{ fontSize: '1.2rem' }}></i>
                                        <span className={`text-sm font-medium line-height-3 ${isActive ? 'text-primary' : 'text-color'}`} style={{ wordBreak: 'break-all' }}>
                                            {file.name}
                                        </span>
                                    </div>
                                    <div className="flex align-items-center gap-3 flex-shrink-0 ml-2">
                                        <i className={`pi pi-chevron-${isActive ? 'up' : 'down'} text-xs text-color-secondary`}></i>
                                    </div>
                                </div>

                                {/* File Content (Thumbnails) - Only visible when active */}
                                {isActive && (
                                    <div className="p-3 bg-white border-top-1 surface-border">
                                        {canvasLoading ? (
                                            <div className="py-4 text-center">
                                                <i className="pi pi-spin pi-spinner text-primary text-xl"></i>
                                            </div>
                                        ) : (
                                            <div className="flex flex-column align-items-center gap-4">
                                                {pageCanvases.map((canvas, index) => (
                                                    <div 
                                                        key={index} 
                                                        className="flex flex-column align-items-center gap-2 cursor-pointer"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const el = document.querySelector(`[data-page-index="${index}"]`);
                                                            el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                        }}
                                                    >
                                                        <div className="border-2 border-primary border-round-sm overflow-hidden shadow-3 transition-all hover:shadow-6" style={{ width: '120px' }}>
                                                            <img 
                                                                src={canvas.toDataURL()} 
                                                                alt={`Trang ${index + 1}`} 
                                                                className="w-full h-auto block" 
                                                            />
                                                        </div>
                                                        <span className="text-xs font-bold text-color-secondary">
                                                            {index + 1}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>


        </div>
    );
};
